import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CatalogCategories, CatalogProducts } from '../components/CatalogBrowse';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { AddToProject } from '../components/AddToProject';
import { useAuth } from '../lib/auth';
import { useCustomer, discountForBrand } from '../lib/customer';
import { useCurrency } from '../lib/currency';
import { supabase, type ProductPublic } from '../lib/supabase';
import { theme } from '../styles/theme';
import { Seo } from '../lib/seo';
import { Price } from '../components/Price';
import { variantPrice } from '../lib/variantPrice';
import { thumbUrl } from '../lib/imageUrl';
import { productModel } from '../lib/productModel';

// Mapeo slug URL → legacy_lutron_line en DB (uppercase)
const LINE_DB_MAP: Record<string, string[]> = {
  caseta:     ['CASETA'],
  radiora3:   ['RADIORA3'],
  radiora2:   ['RADIORA2'],
  wallbox:    ['WALLBOX'],
  maestro:    ['MAESTRO'],
  vive:       ['VIVE'],
  sunnata:    ['SUNNATA'],
  novat:      ['NOVAT'],
  homeworks:  ['HOMEWORKS', 'HOMEWORKS QSX'],
  lumaris:    ['LUMARIS'],
};

type ProductVariant = {
  id: string;
  product_id: string;
  full_sku: string;
  finish: string | null;
  attributes: { color?: string; finish?: string } | null;
  primary_image_url: string | null;
  position: number;
  msrp_usd?: number | null;
  msrp_mxn?: number | null;
  color_temperature_k: number | null;
  beam_angle_deg: number | null;
  wattage_w: number | null;
  dim_method: string | null;
};

// Mapping finish code (Lutron 2-3 chars al final del SKU) → hex
const FINISH_HEX: Record<string, string> = {
  WH: '#F4F4F0', SW: '#FAFAF7', BI: '#E8DEC7', IV: '#EFE5C9', LA: '#D5C9A8',
  AL: '#C8B791', BR: '#5A4733', BL: '#1B1B1B', GR: '#7A7A7A', SL: '#5C6470',
  SA: '#B8A689', CY: '#9C7B5C', MN: '#1F2933', TP: '#8B7A66', CS: '#B5A78C',
  EP: '#3A2A20', DE: '#C5B498', MI: '#A5B0BC', PB: '#2A1F2A', PD: '#A8A6A0',
  LG: '#A0A0A0', SN: '#B8B5A8', SC: '#C8C8C0', BN: '#D4C8A0', TC: '#A55A40',
  CP: '#B87333', GP: '#3A3F45', WG: '#C9A572', HT: '#D03A2A', PM: '#5A2E4F',
  RW: '#EBE8E0', SR: '#A89090', GL: '#E5E7EB', BW: '#A88770', SD: '#B89F75',
  TF: '#5C4632', HN: '#9E9890', CWH: '#F8F8F5', CBL: '#0E0E0E',
  // Códigos sueltos vistos en la base
  SI: '#FAFAF7', QB: '#B89F5C', QZ: '#3A3F45', BB: '#0E0E0E', BE: '#E8DEC7',
  BLA: '#1B1B1B',
};

// Mapping nombre legible (mayoría de variantes lo guardan asi) → hex
// Lutron usa nombres como "Light Almond", "Satin Brass", etc.
const FINISH_NAME_HEX: Record<string, string> = {
  // Básicos
  'negro': '#1B1B1B', 'black': '#1B1B1B', 'gloss black': '#0E0E0E', 'satin black': '#1A1A1A',
  'blanco': '#F4F4F0', 'white': '#F4F4F0', 'gloss white': '#F8F8F5', 'satin white': '#F0F0EC',
  'gris': '#7A7A7A', 'gray': '#7A7A7A', 'grey': '#7A7A7A',
  // Lutron neutros (clamshell colors)
  'ivory': '#EFE5C9', 'gloss ivory': '#F4ECC8', 'satin ivory': '#EAE0C2',
  'almond': '#C8B791', 'light almond': '#D5C9A8', 'gloss light almond': '#DEDA9F',
  'biscuit': '#E8DEC7', 'satin biscuit': '#E8DECA',
  'taupe': '#8B7A66', 'brown': '#5A4733',
  'snow': '#FAFAF7', 'satin snow': '#F0F0EA',
  'midnight': '#1F2933', 'satin midnight': '#2A2F38',
  'palladium': '#A8A6A0', 'mist': '#C8CCD0', 'lunar gray': '#A5B0BC',
  'plum': '#5A2E4F', 'plum black': '#2A1F2A',
  'hot': '#D03A2A',
  'glass': '#E5E7EB',
  'raw white': '#EBE8E0',
  'sand dune': '#B89F75', 'sand': '#B8A689',
  'truffle': '#5C4632',
  'cashmere': '#B5A78C',
  'silver rose': '#A89090',
  'birchwood': '#C9A572',
  'slate': '#5C6470',
  'clay': '#B87333',
  'desert': '#C5B498',
  'espresso': '#3A2A20',
  // Metales
  'bright chrome': '#C0C0C8', 'satin chrome': '#B5B5B0',
  'bright nickel': '#C8C8C5', 'satin nickel': '#B8B5A8',
  'satin brass': '#B89F5C', 'bright brass': '#C9A14A',
  'antique brass': '#8C6E3A', 'antique bronze': '#5C3F22',
  'oil rubbed bronze': '#3A2A20', 'venetian bronze': '#4A2F22',
  'aged copper': '#7A5A3A', 'polished copper': '#B87333',
  'satin gold': '#C0A050', 'polished gold': '#D4AF37',
};

// Devuelve el hex para una variante, intentando varias fuentes
function variantColorHex(v: ProductVariant): string | null {
  // 1. attributes.color (código corto Lutron como "BL", "LA")
  const code = (v.attributes?.color || '').toUpperCase();
  if (code && FINISH_HEX[code]) return FINISH_HEX[code];

  // 2. finish como nombre legible (Black, Light Almond, etc.)
  const name = (v.finish || '').toLowerCase().trim();
  if (name && FINISH_NAME_HEX[name]) return FINISH_NAME_HEX[name];

  // 3. finish como código corto (cuando se guardó "BL" en finish)
  const codeFromFinish = (v.finish || '').toUpperCase().trim();
  if (codeFromFinish && FINISH_HEX[codeFromFinish]) return FINISH_HEX[codeFromFinish];

  // 4. Sufijo del SKU (Lutron termina con código de color)
  const skuParts = v.full_sku.split('-');
  const last = skuParts[skuParts.length - 1]?.toUpperCase();
  if (last && FINISH_HEX[last]) return FINISH_HEX[last];

  return null;
}

// Construye los picks (uno por valor único) para cada dimensión que varía
type VariantDimension = 'color' | 'cct' | 'beam' | 'control';
type DimensionPicks = {
  dim: VariantDimension;
  picks: ProductVariant[];
};

function buildDimensionPicks(variants: ProductVariant[]): DimensionPicks[] {
  if (variants.length < 2) return [];
  const result: DimensionPicks[] = [];

  // Color (finish)
  const seenColor = new Set<string>();
  const colorPicks = variants
    .filter(v => v.finish || v.attributes?.color)
    .filter(v => {
      const key = (v.finish ?? v.attributes?.color ?? '').toLowerCase();
      if (!key || seenColor.has(key)) return false;
      seenColor.add(key);
      return true;
    })
    .sort((a, b) => a.position - b.position);
  if (colorPicks.length > 1) result.push({ dim: 'color', picks: colorPicks });

  // CCT
  const seenCct = new Set<number>();
  const cctPicks = variants
    .filter(v => v.color_temperature_k != null)
    .filter(v => {
      const k = v.color_temperature_k!;
      if (seenCct.has(k)) return false;
      seenCct.add(k);
      return true;
    })
    .sort((a, b) => (a.color_temperature_k ?? 0) - (b.color_temperature_k ?? 0));
  if (cctPicks.length > 1) result.push({ dim: 'cct', picks: cctPicks });

  // Beam angle
  const seenBeam = new Set<number>();
  const beamPicks = variants
    .filter(v => v.beam_angle_deg != null)
    .filter(v => {
      const b = v.beam_angle_deg!;
      if (seenBeam.has(b)) return false;
      seenBeam.add(b);
      return true;
    })
    .sort((a, b) => (a.beam_angle_deg ?? 0) - (b.beam_angle_deg ?? 0));
  if (beamPicks.length > 1) result.push({ dim: 'beam', picks: beamPicks });

  // Control. La potencia puede depender de este eje, pero nunca se ofrece
  // como un selector separado.
  const seenControl = new Set<string>();
  const controlPicks = variants
    .filter(v => v.dim_method)
    .filter(v => {
      const control = v.dim_method!;
      if (seenControl.has(control)) return false;
      seenControl.add(control);
      return true;
    })
    .sort((a, b) => (a.dim_method ?? '').localeCompare(b.dim_method ?? ''));
  if (controlPicks.length > 1) result.push({ dim: 'control', picks: controlPicks });

  return result;
}

function dimensionLabel(dim: VariantDimension, n: number): string {
  if (dim === 'color')   return `${n} acabado${n === 1 ? '' : 's'}`;
  if (dim === 'cct')     return 'Temperatura';
  if (dim === 'beam')    return 'Apertura';
  if (dim === 'control') return 'Control';
  return '';
}

function variantValueLabel(v: ProductVariant, dim: VariantDimension): string {
  if (dim === 'color')   return v.finish ?? v.attributes?.color ?? v.full_sku;
  if (dim === 'cct')     return `${v.color_temperature_k}K`;
  if (dim === 'beam')    return `${v.beam_angle_deg}°`;
  if (dim === 'control') return v.dim_method ?? '';
  return v.full_sku;
}

function finishKey(v: ProductVariant | null | undefined): string {
  const raw = (v?.finish ?? v?.attributes?.finish ?? v?.attributes?.color ?? '').trim().toLowerCase();
  if (['blanco', 'white', 'b', 'wh', 'sw'].includes(raw)) return 'white';
  if (['negro', 'black', 'n', 'bl', 'bk'].includes(raw)) return 'black';
  return raw;
}

function finishKeyFromImage(url: string | null | undefined): string {
  const value = (url ?? '').toLowerCase();
  if (/white|blanco/.test(value)) return 'white';
  if (/black|negro/.test(value)) return 'black';
  return '';
}

function alternateColorVariant(
  variants: ProductVariant[],
  current: ProductVariant | null,
  currentImageUrl: string | null | undefined,
): ProductVariant | null {
  const currentKey = finishKey(current) || finishKeyFromImage(currentImageUrl);
  const targetKey = currentKey === 'white' ? 'black' : currentKey === 'black' ? 'white' : '';
  const candidates = variants.filter(v => {
    if (!v.primary_image_url || v.primary_image_url === currentImageUrl) return false;
    const key = finishKey(v);
    return key && key !== currentKey && (!targetKey || key === targetKey);
  });
  if (!candidates.length) return null;

  return [...candidates].sort((a, b) => {
    const score = (v: ProductVariant) => {
      if (!current) return 0;
      let value = 0;
      if (v.color_temperature_k === current.color_temperature_k) value += 4;
      if (v.beam_angle_deg === current.beam_angle_deg) value += 3;
      if (Number(v.wattage_w) === Number(current.wattage_w)) value += 2;
      return value;
    };
    return score(b) - score(a) || a.position - b.position;
  })[0];
}

function alternateGalleryImage(
  images: ProductPublic['images'],
  currentImageUrl: string | null | undefined,
): string | null {
  const currentKey = finishKeyFromImage(currentImageUrl);
  const targetKey = currentKey === 'white' ? 'black' : currentKey === 'black' ? 'white' : '';
  if (!targetKey) return null;
  return images.find(image => image.url !== currentImageUrl && finishKeyFromImage(image.url) === targetKey)?.url ?? null;
}

// Mapeo cat slug legacy del menú → category_path / product_type fallback
// Para URLs nuevas usamos ?path= con el path real (Mangueras y tiras LED/127V/COB)
const LEGACY_CAT_TO_PATH: Record<string, string> = {
  'mangueras-tiras':  'Mangueras y tiras LED',
  'riel-magnetico':   'Riel Magnético',
  'downlights':       'Downlights',
  'exterior':         'Exterior',
  'fuente-poder':     'Fuente de poder',
  'proyectores':      'Proyectores',
  'series':           'Series',
  'accesorios':       'Accesorios',
};

const LUTRON_TYPE_MAP: Record<string, string[]> = {
  'dimmers':      ['lutron_dimmer'],
  'switches':     ['lutron_switch'],
  'keypads':      ['lutron_keypad'],
  'picos':        ['lutron_pico'],
  'procesadores': ['lutron_processor'],
  'sensores':     ['lutron_sensor'],
  'wallplates':   ['lutron_wallplate'],
  'shades':       ['lutron_shade'],
};

const HIDE_TECH_FILTERS = new Set(['Accesorios']);

type FilterState = {
  brand: 'all' | 'nuled' | 'lutron';
  voltaje: Set<string>;   // '127V', '24V', '48V'
  ip: Set<string>;        // '20', '54', '65', '67'
  cct: Set<string>;       // '2700K', '3000K', '4000K'
  lutronOnly: boolean;
  potenciaMax?: number;
};

const VOLTAJE_OPTS = ['24V', '48V', '127V'];
const IP_OPTS = ['20', '54', '65', '67'];
const CCT_OPTS = ['2700K', '3000K', '4000K'];

export function Catalog() {
  const [params, setParams] = useSearchParams();
  const requestedPath = params.get('path') ?? LEGACY_CAT_TO_PATH[params.get('cat') ?? ''] ?? '';
  const currentPath = requestedPath.split('/')[0];
  const selectedSubs = [...new Set(params.has('sub') ? params.getAll('sub') : requestedPath.includes('/') ? [requestedPath] : [])].filter(p => currentPath && p.startsWith(currentPath + '/'));
  const selectedSubsKey = JSON.stringify(selectedSubs);
  const setSubs = (values: string[]) => {
    const next = new URLSearchParams(params);
    next.delete('sub');
    if (currentPath) next.set('path', currentPath);
    values.forEach(value => next.append('sub', value));
    setParams(next, { replace: true, preventScrollReset: true });
  };
  const toggleSub = (path: string) => setSubs(selectedSubs.includes(path) ? selectedSubs.filter(p => p !== path) : [...selectedSubs.filter(p => !p.startsWith(path + '/') && !path.startsWith(p + '/')), path]);
  const initialQ = params.get('q') ?? '';
  const initialCat = params.get('cat') ?? '';
  const initialBrand = (params.get('brand') ?? 'all') as FilterState['brand'];
  const lutronLine = params.get('line') ?? '';  // caseta, radiora3, wallbox, maestro, vive

  const { session } = useAuth();
  const { customer } = useCustomer();
  const tierLabel = customer?.tier_name ?? null;

  const [items, setItems] = useState<ProductPublic[]>([]);
  const [variantsByProduct, setVariantsByProduct] = useState<Record<string, ProductVariant[]>>({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(initialQ);
  const [filters, setFilters] = useState<FilterState>({
    brand: initialBrand,
    voltaje: new Set(),
    ip: new Set(),
    cct: new Set(),
    lutronOnly: false,
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    // Server-side filter por brand + línea Lutron + product_type (cat)
    let query = supabase
      .from('products_public')
      .select('id, sku, brand_code, name, short_description, description, msrp_mxn, msrp_usd, specs, images, tags, status, lutron_compatible, unit, legacy_lutron_line, product_type, category_paths')
      .eq('status', 'active')
      .order('msrp_mxn', { ascending: false, nullsFirst: false });

    if (initialBrand !== 'all') {
      query = query.eq('brand_code', initialBrand);
    }
    if (lutronLine && LINE_DB_MAP[lutronLine]) {
      query = query.in('legacy_lutron_line', LINE_DB_MAP[lutronLine]);
    }
    // Categoría: prioridad ?path= (jerarquía real) → ?cat= legacy → Lutron product_type
    const pathParam = currentPath;
    if (pathParam && initialBrand !== 'lutron') {
      // contains category_path (PostgREST: cs operator with array)
      query = query.contains('category_paths', [pathParam]);
    } else if (initialCat) {
      if (initialBrand === 'lutron' && LUTRON_TYPE_MAP[initialCat]) {
        query = query.in('product_type', LUTRON_TYPE_MAP[initialCat]);
      } else if (LEGACY_CAT_TO_PATH[initialCat]) {
        query = query.contains('category_paths', [LEGACY_CAT_TO_PATH[initialCat]]);
      }
    }

    query.limit(2000).then(async ({ data, error }) => {
      if (!alive) return;
      if (error) console.error(error);
      const products = (data as ProductPublic[]) ?? [];
      setItems(products);
      setLoading(false);

      // Cargar variants para productos con has_variants para mostrar swatches
      const productIds = products.map(p => p.id);
      if (productIds.length > 0) {
        const { data: varData } = await supabase
          .from('product_variants')
          .select('id, product_id, full_sku, finish, attributes, primary_image_url, position, color_temperature_k, beam_angle_deg, wattage_w, dim_method, msrp_usd, msrp_mxn')
          .in('product_id', productIds.slice(0, 500))  // limit a primeros 500 productos visibles
          .eq('active', true)
          .order('position');
        if (alive) {
          const byProd: Record<string, ProductVariant[]> = {};
          for (const v of (varData as ProductVariant[] | null) ?? []) {
            if (!byProd[v.product_id]) byProd[v.product_id] = [];
            byProd[v.product_id].push(v);
          }
          setVariantsByProduct(byProd);
        }
      }
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBrand, lutronLine, initialCat, currentPath]);

  const filtered = useMemo(() => {
    const qn = q.trim().toLowerCase();
    return items.filter(p => {
      // brand (server ya filtró, pero por seguridad si el user cambia el tab)
      if (filters.brand !== 'all' && p.brand_code !== filters.brand) return false;

      // lutron compatible
      if (filters.lutronOnly && !p.lutron_compatible) return false;

      // text query
      if (qn) {
        const matches =
          p.name.toLowerCase().includes(qn) ||
          p.sku.toLowerCase().includes(qn) ||
          productModel(p).toLowerCase().includes(qn) ||
          (p.short_description ?? '').toLowerCase().includes(qn);
        if (!matches) return false;
      }

      if (selectedSubs.length && !p.category_paths?.some(path => selectedSubs.some(sub => path === sub || path.startsWith(sub + '/')))) return false;

      // voltaje
      if (filters.voltaje.size > 0) {
        const v = String(p.specs?.voltaje ?? '').toUpperCase();
        const match = Array.from(filters.voltaje).some(opt =>
          v.includes(opt.replace('V', '')) || v.includes(opt)
        );
        if (!match) return false;
      }

      // IP
      if (filters.ip.size > 0) {
        const ip = String(p.specs?.ip ?? '');
        if (!Array.from(filters.ip).includes(ip)) return false;
      }

      // CCT
      if (filters.cct.size > 0) {
        const cct = String(p.specs?.temperatura_de_color ?? p.specs?.cct ?? '').toUpperCase();
        const match = Array.from(filters.cct).some(opt => cct.includes(opt));
        if (!match) return false;
      }

      return true;
    });
  }, [items, q, filters, lutronLine, selectedSubsKey]);

  const toggleSet = (key: 'voltaje' | 'ip' | 'cct', val: string) => {
    setFilters(f => {
      const s = new Set(f[key]);
      if (s.has(val)) s.delete(val);
      else s.add(val);
      return { ...f, [key]: s };
    });
  };

  const setBrand = (b: FilterState['brand']) => {
    setFilters(f => ({ ...f, brand: b }));
    if (b === 'all') params.delete('brand');
    else params.set('brand', b);
    // Si cambia brand y no es lutron, eliminar el filtro de línea
    if (b !== 'lutron') params.delete('line');
    setParams(params, { replace: true });
  };

  const activeFilterCount =
    selectedSubs.length + filters.voltaje.size + filters.ip.size + filters.cct.size +
    (filters.lutronOnly ? 1 : 0) +
    (filters.brand !== 'all' ? 1 : 0);

  const lutronLineLabel = lutronLine
    ? lutronLine === 'radiora3' ? 'RadioRA 3'
    : lutronLine.charAt(0).toUpperCase() + lutronLine.slice(1)
    : '';
  const title = lutronLine
    ? `Lutron ${lutronLineLabel} · Catálogo NULED`
    : initialCat
      ? `${initialCat.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} · NULED`
      : `${currentPath || 'Catálogo'} · NULED Iluminación LED`;

  return (
    <div style={{ background: theme.bg, color: theme.text, fontFamily: theme.font.sans }}>
      <Seo
        title={title}
        description={`Catálogo NULED. ${items.length}+ luminarias LED para distribuidores e integradores en México. Downlights, riel magnético, exterior, mangueras y tiras LED. Compatible Lutron.`}
        canonical={`https://nuled.mx/catalogo${currentPath ? `?path=${encodeURIComponent(currentPath)}` : initialCat ? `?cat=${initialCat}` : ''}`}
      />
      <Header />

      <section style={{ maxWidth: theme.maxWidth, margin: '0 auto', padding: '40px 24px' }}>
        <nav className="cb-breadcrumb" aria-label="Ubicación"><Link to="/">Inicio</Link><span>/</span><Link to="/catalogo">Catálogo</Link>{currentPath.split('/').filter(Boolean).map((part,i) => <span key={i}> / <Link to={`/catalogo?path=${encodeURIComponent(currentPath.split('/').slice(0,i+1).join('/'))}`}>{part}</Link></span>)}</nav>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <h1 style={{
            fontFamily: theme.font.head,
            fontSize: 32,
            fontWeight: 400,
            color: theme.brand,
            margin: 0,
          }}>
            {lutronLine
              ? `Lutron ${lutronLineLabel}`
              : initialCat
                ? initialCat.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                : currentPath.split('/').at(-1) || 'Catálogo'}
          </h1>
          <span style={{ color: theme.textMuted, fontSize: 14 }}>
            {loading ? 'Cargando…' : `${filtered.length} de ${items.length} productos`}
          </span>
          {lutronLine && (
            <Link
              to={`/lutron/${lutronLine}`}
              style={{
                fontSize: 12,
                color: theme.accentDark,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                textDecoration: 'none',
                marginLeft: 'auto',
              }}
            >
              Sobre Lutron {lutronLineLabel} →
            </Link>
          )}
        </div>

        {initialBrand !== 'lutron' && !loading && currentPath !== 'COMPOSE' && <CatalogCategories items={items} path={currentPath} selected={selectedSubs} onToggle={toggleSub} />}

        {selectedSubs.length > 0 && <div className="cb-active-filters" aria-label="Subcategorías seleccionadas">
          {selectedSubs.map(sub => <button key={sub} onClick={() => toggleSub(sub)} aria-label={`Quitar ${sub.split('/').slice(1).join(' / ')}`}>{sub.split('/').slice(1).join(' / ')} <span aria-hidden="true">×</span></button>)}
          <button onClick={() => setSubs([])}>Ver todas las subcategorías</button>
        </div>}
        {/* Search inline */}
        <div style={{ display: 'flex', gap: 12, margin: '24px 0', flexWrap: 'wrap' }}>
          <input
            placeholder="Buscar por nombre o modelo"
            value={q}
            onChange={e => setQ(e.target.value)}
            style={{
              flex: 1,
              minWidth: 240,
              background: theme.bg,
              border: `1px solid ${theme.border}`,
              padding: '12px 16px',
              color: theme.text,
              fontSize: 14,
              fontFamily: theme.font.sans,
              outline: 'none',
            }}
          />
          <BrandTab active={filters.brand === 'all'} onClick={() => setBrand('all')}>Todas</BrandTab>
          <BrandTab active={filters.brand === 'nuled'} onClick={() => setBrand('nuled')}>NULED</BrandTab>
          <BrandTab active={filters.brand === 'lutron'} onClick={() => setBrand('lutron')}>Lutron</BrandTab>
        </div>

        {!session && (
          <div style={{
            background: theme.bgFooter,
            border: `1px solid ${theme.border}`,
            borderLeft: `4px solid ${theme.accent}`,
            padding: '14px 18px',
            fontSize: 14,
            color: theme.brand,
            marginBottom: 24,
          }}>
            <strong>¿Eres distribuidor o integrador?</strong>{' '}
            <Link to="/login" style={{ color: theme.accentDark, fontWeight: 700 }}>
              Inicia sesión
            </Link>{' '}
            para conocer tus precios mayoristas.
          </div>
        )}

        {/* Mobile filters toggle */}
        <button
          onClick={() => setShowFilters(s => !s)}
          style={{
            display: 'none',
            ...(typeof window !== 'undefined' && window.innerWidth < 768 ? { display: 'inline-block' } : {}),
            background: theme.brand,
            color: theme.textOnBrand,
            border: 'none',
            padding: '10px 16px',
            fontSize: 12,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            marginBottom: 16,
          }}
          className="filter-toggle-mobile"
        >
          {showFilters ? 'Ocultar filtros' : `Filtros${activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}`}
        </button>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 240px) 1fr', gap: 32 }} className="catalog-grid">
          {/* Filters sidebar */}
          <aside className={showFilters ? 'filters-open' : 'filters-closed'} style={{ minWidth: 0 }}>
            {/* Sub-categorías NULED dinámicas según el path actual */}
            {initialBrand !== 'lutron' && (
              <CategoryTree items={items} currentPath={currentPath} selected={selectedSubs} onToggle={toggleSub}
                            onSelect={(p) => {
                              if (p) params.set('path', p); else params.delete('path');
                              params.delete('cat');
                              params.delete('sub');
                              setParams(params, { replace: true });
                            }} />
            )}

            {!HIDE_TECH_FILTERS.has(LEGACY_CAT_TO_PATH[initialCat] ?? '') && initialBrand !== 'lutron' && (
              <>
                <FilterGroup title="Voltaje">
                  {VOLTAJE_OPTS.map(opt => (
                    <Chip
                      key={opt}
                      active={filters.voltaje.has(opt)}
                      onClick={() => toggleSet('voltaje', opt)}
                    >
                      {opt}
                    </Chip>
                  ))}
                </FilterGroup>

                <FilterGroup title="IP (protección)">
                  {IP_OPTS.map(opt => (
                    <Chip
                      key={opt}
                      active={filters.ip.has(opt)}
                      onClick={() => toggleSet('ip', opt)}
                    >
                      IP{opt}
                    </Chip>
                  ))}
                </FilterGroup>

                <FilterGroup title="Temperatura de color">
                  {CCT_OPTS.map(opt => (
                    <Chip
                      key={opt}
                      active={filters.cct.has(opt)}
                      onClick={() => toggleSet('cct', opt)}
                    >
                      {opt}
                    </Chip>
                  ))}
                </FilterGroup>
              </>
            )}

            {/* Filtros Lutron por tipo de producto */}
            {initialBrand === 'lutron' && (
              <FilterGroup title="Tipo de producto">
                <LutronTypeChips currentCat={initialCat} onSelect={(slug) => {
                  if (slug) params.set('cat', slug); else params.delete('cat');
                  setParams(params, { replace: true });
                }} />
              </FilterGroup>
            )}

            <FilterGroup title="Compatibilidad">
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
                color: theme.brand,
                cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={filters.lutronOnly}
                  onChange={e => setFilters(f => ({ ...f, lutronOnly: e.target.checked }))}
                  style={{ accentColor: theme.accent }}
                />
                Solo compatible Lutron
              </label>
            </FilterGroup>

            {activeFilterCount > 0 && (
              <button
                onClick={() => {
                  setFilters({ brand: 'all', voltaje: new Set(), ip: new Set(), cct: new Set(), lutronOnly: false });
                  const next = new URLSearchParams(params);
                  ['sub', 'brand', 'line'].forEach(key => next.delete(key));
                  if (currentPath) next.set('path', currentPath);
                  setParams(next, { replace: true, preventScrollReset: true });
                }}
                style={{
                  marginTop: 16,
                  background: 'transparent',
                  border: `1px solid ${theme.border}`,
                  color: theme.brand,
                  padding: '8px 14px',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  fontFamily: theme.font.sans,
                  width: '100%',
                }}
              >
                Limpiar filtros ({activeFilterCount})
              </button>
            )}
          </aside>

          {/* Grid */}
          <div>
            {loading ? (
              <div className="product-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: 16,
              }}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} style={{ border: `1px solid ${theme.border}` }}>
                    <div style={{ aspectRatio: '1 / 1', background: theme.bgImage }} />
                    <div style={{ padding: 16 }}>
                      <div style={{ ...skeleton, height: 10, width: '40%' }} />
                      <div style={{ ...skeleton, height: 14, width: '90%', marginTop: 8 }} />
                      <div style={{ ...skeleton, height: 14, width: '60%', marginTop: 8 }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '64px 24px',
                color: theme.textMuted,
              }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
                <div style={{ color: theme.brand, fontWeight: 600, fontSize: 16 }}>
                  No encontramos productos con esos filtros.
                </div>
                <div style={{ marginTop: 8, fontSize: 14 }}>
                  Prueba quitar algunos filtros o{' '}
                  <a
                    href={`https://wa.me/525549332582?text=Hola%20NULED,%20busco:%20${encodeURIComponent(q)}`}
                    style={{ color: theme.accentDark, fontWeight: 700 }}
                  >
                    pídelo por WhatsApp
                  </a>.
                </div>
              </div>
            ) : (
              <CatalogProducts key={currentPath} items={filtered} path={currentPath} render={(p, idx) => (
                  <ProductCard
                    key={p.id}
                    p={p}
                    loggedIn={!!session}
                    tierDiscountPct={discountForBrand(customer, p.brand_code)}
                    tierLabel={tierLabel}
                    variants={variantsByProduct[p.id] ?? []}
                    eager={idx < 8}
                  />
              )} />
            )}
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @media (max-width: 768px) {
          .catalog-grid { grid-template-columns: 1fr !important; }
          .filters-closed { display: none; }
          .filter-toggle-mobile { display: inline-block !important; }
        }
        @media (max-width: 680px) {
          .product-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 10px !important; }
          .product-card .pc-body { padding: 10px !important; }
          .product-card .pc-img { padding: 10px !important; }
          .product-card .pc-name { font-size: 12.5px !important; min-height: 34px !important; }
        }
      `}</style>
    </div>
  );
}

function FilterGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: 1,
        fontWeight: 700,
        color: theme.brand,
        marginBottom: 12,
      }}>
        {title}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {children}
      </div>
    </div>
  );
}

type CatRow = { path: string; depth: number; count: number };

function CategoryTree({ items, currentPath, onSelect, selected, onToggle }: { items: ProductPublic[]; currentPath: string; onSelect: (p: string) => void; selected: string[]; onToggle: (p: string) => void }) {
  const [rows, setRows] = useState<CatRow[]>([]);
  useEffect(() => {
    supabase.rpc('nuled_category_tree').then(({ data }) => {
      setRows(((data as any[]) ?? []).map(r => ({ path: r.path, depth: Number(r.depth), count: Number(r.count) })));
    });
  }, []);

  if (rows.length === 0) return null;

  // Si hay currentPath, mostrar: parent (link back to root) + siblings + children
  // Si no, mostrar solo top-level
  const top = rows.filter(r => r.depth === 1);
  const currentParts = currentPath ? currentPath.split('/') : [];
  const children = currentPath
    ? rows.filter(r => r.path.startsWith(currentPath + '/')).map(r => ({ ...r, count: items.filter(p => p.category_paths?.some(path => path === r.path || path.startsWith(r.path + '/'))).length })).filter(r => r.count > 0)
    : [];

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700,
        color: theme.brand, marginBottom: 12,
      }}>Categorías</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {!currentPath ? top.map(r => (
          <CatLink key={r.path} label={r.path} count={r.count} onClick={() => onSelect(r.path)} />
        )) : (
          <>
            <button onClick={() => onSelect('')} style={catBackBtn}>
              ← Todas las categorías
            </button>
            {currentParts.length > 1 && (
              <button
                onClick={() => onSelect(currentParts.slice(0, -1).join('/'))}
                style={catBackBtn}
              >
                ↑ {currentParts.slice(0, -1).join(' / ')}
              </button>
            )}
            <div style={{
              padding: '8px 10px',
              background: theme.brand,
              color: theme.textOnBrand,
              fontSize: 12,
              fontWeight: 700,
            }}>
              {currentParts[currentParts.length - 1]}
            </div>
            {children.length > 0 && (
              <div style={{ paddingLeft: 8 }}>
                {children.map(r => (
                  <label key={r.path} className="cb-sub-filter" style={{ paddingLeft: (r.depth - currentParts.length - 1) * 12 }}>
                    <input type="checkbox" checked={selected.includes(r.path)} onChange={() => onToggle(r.path)} />
                    <span>{r.path.split('/').pop()}</span><small>{r.count}</small>
                  </label>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CatLink({ label, count, onClick }: { label: string; count: number; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      background: 'transparent', border: 'none',
      padding: '6px 0',
      fontSize: 13, color: theme.brand,
      cursor: 'pointer',
      fontFamily: theme.font.sans,
      textAlign: 'left',
    }}>
      <span>{label}</span>
      <span style={{ fontSize: 10, color: theme.textMuted, fontWeight: 600 }}>{count}</span>
    </button>
  );
}

const catBackBtn: React.CSSProperties = {
  background: 'transparent', border: 'none', textAlign: 'left',
  padding: '6px 0', fontSize: 11, color: theme.textMuted, cursor: 'pointer',
  fontFamily: theme.font.sans, fontWeight: 600,
};

const LUTRON_TYPE_OPTS = [
  { slug: 'dimmers',      label: 'Atenuadores (dimmer)' },
  { slug: 'switches',     label: 'Switches' },
  { slug: 'keypads',      label: 'Keypads / botoneras' },
  { slug: 'picos',        label: 'Pico (remoto)' },
  { slug: 'procesadores', label: 'Procesadores / hubs' },
  { slug: 'sensores',     label: 'Sensores' },
  { slug: 'wallplates',   label: 'Tapas / placas' },
  { slug: 'shades',       label: 'Persianas' },
];

function LutronTypeChips({ currentCat, onSelect }: { currentCat: string; onSelect: (s: string) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      <Chip active={!currentCat} onClick={() => onSelect('')}>Todos</Chip>
      {LUTRON_TYPE_OPTS.map(opt => (
        <Chip key={opt.slug} active={currentCat === opt.slug} onClick={() => onSelect(opt.slug)}>
          {opt.label}
        </Chip>
      ))}
    </div>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: active ? theme.brand : theme.bg,
      color: active ? theme.textOnBrand : theme.brand,
      border: `1px solid ${active ? theme.brand : theme.border}`,
      padding: '6px 12px',
      fontSize: 12,
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: theme.font.sans,
    }}>
      {children}
    </button>
  );
}

function BrandTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: active ? theme.brand : theme.bg,
      color: active ? theme.textOnBrand : theme.brand,
      border: `1px solid ${active ? theme.brand : theme.border}`,
      padding: '10px 18px',
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.5,
      textTransform: 'uppercase',
      cursor: 'pointer',
      fontFamily: theme.font.sans,
    }}>
      {children}
    </button>
  );
}

function ProductCard({ p, loggedIn, tierDiscountPct, tierLabel, variants, eager = false }: {
  p: ProductPublic;
  loggedIn: boolean;
  tierDiscountPct: number;
  tierLabel: string | null;
  variants: ProductVariant[];
  eager?: boolean;
}) {
  const { usdMxn } = useCurrency();
  const defaultImg = p.images?.find(i => i.is_thumbnail) ?? p.images?.[0];

  // Construir picks para CADA dimensión que varía (color + cct + beam + wattage)
  const dimensionPicks = buildDimensionPicks(variants);
  const [activeVariant, setActiveVariant] = useState<ProductVariant | null>(null);
  const [previewAlternate, setPreviewAlternate] = useState(false);
  const img = activeVariant?.primary_image_url ? { url: activeVariant.primary_image_url, is_thumbnail: false } : defaultImg;
  const displayedVariant = activeVariant ?? variants.find(v => v.primary_image_url === img?.url) ?? null;
  const alternateVariant = alternateColorVariant(variants, displayedVariant, img?.url);
  const alternateImageUrl = alternateVariant?.primary_image_url ?? alternateGalleryImage(p.images ?? [], img?.url);

  const { usd: selectedUsd, mxn: selectedMxn } = variantPrice(p, activeVariant, variants);

  // Cálculo de precio con tier
  const hasTier = loggedIn && tierDiscountPct > 0;
  const factor = 1 - (tierDiscountPct / 100);
  const netoUsd = selectedUsd != null ? selectedUsd * factor : null;
  const netoMxn = netoUsd != null && usdMxn ? netoUsd * usdMxn : (selectedMxn != null ? selectedMxn * factor : null);

  return (
    <div className="product-card" style={{
      background: theme.bg,
      border: `1px solid ${theme.border}`,
      display: 'flex',
      flexDirection: 'column',
    }}>
      <Link to={`/producto/${encodeURIComponent(p.sku)}${activeVariant ? `?v=${encodeURIComponent(activeVariant.full_sku)}` : ''}`} style={{ textDecoration: 'none', color: theme.text }}>
        <div
          className="pc-media"
          onMouseEnter={() => alternateImageUrl && setPreviewAlternate(true)}
          onMouseLeave={() => setPreviewAlternate(false)}
          style={{
          width: '100%',
          aspectRatio: '1 / 1',
          backgroundColor: '#FFFFFF',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {img && (
            <img
              className="pc-img pc-img-primary"
              src={thumbUrl(img.url, 300)}
              alt={p.name}
              loading={eager ? 'eager' : 'lazy'}
              {...(eager ? { fetchPriority: 'high' as any } : {})}
              decoding="async"
              style={{
                position: 'absolute',
                top: 0, left: 0,
                width: '100%', height: '100%',
                objectFit: 'contain',
                padding: 16,
                boxSizing: 'border-box',
                opacity: previewAlternate && alternateImageUrl ? 0 : 1,
                transition: 'opacity 220ms ease',
              }}
            />
          )}
          {alternateImageUrl && (
            <img
              className="pc-img pc-img-alternate"
              src={thumbUrl(alternateImageUrl, 300)}
              alt={`${p.name} · ${alternateVariant?.finish ?? 'acabado alterno'}`}
              loading="lazy"
              decoding="async"
              aria-hidden={!previewAlternate}
              style={{
                position: 'absolute',
                top: 0, left: 0,
                width: '100%', height: '100%',
                objectFit: 'contain',
                padding: 16,
                boxSizing: 'border-box',
                opacity: previewAlternate ? 1 : 0,
                transition: 'opacity 220ms ease',
              }}
            />
          )}
        </div>
        <div className="pc-body" style={{ padding: 16, borderTop: `1px solid ${theme.border}` }}>
          <div style={{
            fontSize: 11,
            color: theme.textMuted,
            fontFamily: theme.font.mono,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}>
            Modelo: {productModel(p)}
          </div>
          <div className="pc-name" style={{
            fontSize: 14,
            color: theme.brand,
            marginTop: 8,
            minHeight: 38,
            lineHeight: 1.35,
          }}>
            {p.name}
          </div>
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', minHeight: 22, gap: 8 }}>
            {!loggedIn ? (
              <span style={{ fontSize: 12, color: theme.brand, fontStyle: 'italic' }}>
                Inicia sesión para precio
              </span>
            ) : hasTier && netoUsd != null ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div style={{
                  fontSize: 9,
                  color: theme.accentDark,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}>
                  Tu precio · {tierLabel ?? `−${tierDiscountPct}%`}
                </div>
                <Price usd={netoUsd} mxn={netoMxn ?? undefined} size="sm" showSecondary />
                {selectedUsd != null && (
                  <div style={{
                    fontSize: 10,
                    color: theme.textMuted,
                    marginTop: 2,
                  }}>
                    Precio sugerido al cliente: <strong style={{ color: theme.brand }}>${selectedUsd!.toLocaleString('en-US', { maximumFractionDigits: 2 })} USD</strong>
                  </div>
                )}
              </div>
            ) : (
              <Price usd={selectedUsd} mxn={selectedMxn} size="sm" showSecondary />
            )}
            {p.lutron_compatible && (
              <span style={{
                fontSize: 9,
                color: theme.brandTag.lutron,
                border: `1px solid ${theme.brandTag.lutron}`,
                padding: '2px 6px',
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
              }}>
                Lutron
              </span>
            )}
          </div>
        </div>
      </Link>

      {/* Selectores de variante — uno por cada dimensión que varía */}
      {dimensionPicks.length > 0 && (
        <div style={{ padding: '0 16px 8px', display: 'grid', gap: 8 }}>
          {dimensionPicks.map(({ dim, picks }) => {
            const max = dim === 'color' ? 8 : 6;
            const visible = picks.slice(0, max);

            return (
              <div key={dim}>
                <div style={{
                  fontSize: 9, color: theme.textMuted, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4,
                }}>
                  {dimensionLabel(dim, picks.length)}
                </div>

                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {visible.map(v => {
                    const isActive = activeVariant?.id === v.id;
                    const onClickPick = (e: React.MouseEvent) => {
                      e.preventDefault();
                      setActiveVariant(activeVariant?.id === v.id ? null : v);
                    };
                    const label = variantValueLabel(v, dim);

                    if (dim === 'color') {
                      const hex = variantColorHex(v) ?? '#D8D2C4';
                      const isLight = !!hex.toUpperCase().match(/^#(F|E)/);
                      return (
                        <button
                          key={v.id}


                          onClick={onClickPick}
                          title={`${label} (${v.full_sku})`}
                          aria-label={`Acabado ${label}`}
                          style={{
                            width: 20, height: 20,
                            background: hex,
                            border: isActive
                              ? `2px solid ${theme.brand}`
                              : `1px solid ${isLight ? theme.borderStrong : theme.border}`,
                            borderRadius: '50%',
                            cursor: 'pointer',
                            padding: 0,
                            boxShadow: isActive ? `0 0 0 2px ${theme.accent}` : 'none',
                          }}
                        />
                      );
                    }

                    // pills (cct / beam / control)
                    return (
                      <button
                        key={v.id}


                        onClick={onClickPick}
                        title={`${label} (${v.full_sku})`}
                        style={{
                          background: isActive ? theme.brand : theme.bg,
                          color:      isActive ? '#FFFFFF' : theme.brand,
                          border: `1px solid ${isActive ? theme.brand : theme.borderStrong}`,
                          padding: '3px 8px',
                          fontSize: 11,
                          fontWeight: 700,
                          fontFamily: theme.font.mono,
                          cursor: 'pointer',
                          letterSpacing: 0.3,
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                  {picks.length > max && (
                    <span style={{
                      fontSize: 10, color: theme.textMuted,
                      fontWeight: 700, alignSelf: 'center',
                      marginLeft: 4,
                    }}>+{picks.length - max}</span>
                  )}
                </div>
              </div>
            );
          })}

          {activeVariant && (
            <div style={{ fontSize: 10, color: theme.textMuted }}>
              <span style={{ fontFamily: theme.font.mono }}>{activeVariant.full_sku}</span>
            </div>
          )}
        </div>
      )}

      <div style={{ padding: '0 16px 14px' }}>
        <AddToProject
          variant="compact"
          product={{
            product_id: p.id,
            sku: activeVariant?.full_sku ?? p.sku,
            name: activeVariant
              ? (() => {
                  // Construir descriptor con todas las dimensiones que tenga la variante activa
                  const parts: string[] = [];
                  if (activeVariant.finish || activeVariant.attributes?.color) {
                    parts.push(activeVariant.finish ?? activeVariant.attributes!.color!);
                  }
                  if (activeVariant.color_temperature_k != null) parts.push(`${activeVariant.color_temperature_k}K`);
                  if (activeVariant.beam_angle_deg != null) parts.push(`${activeVariant.beam_angle_deg}°`);
                  if (activeVariant.wattage_w != null) parts.push(`${activeVariant.wattage_w}W`);
                  return parts.length > 0 ? `${p.name} · ${parts.join(' · ')}` : p.name;
                })()
              : p.name,
            brand_code: p.brand_code,
            msrp_mxn: selectedMxn,
            msrp_usd: selectedUsd,
            cost_usd: hasTier && netoUsd != null ? netoUsd : selectedUsd,
            price_pending: selectedUsd == null && selectedMxn == null,
            image_url: img?.url ?? null,
            unit: p.unit,
          }}
        />
      </div>
    </div>
  );
}

const skeleton: React.CSSProperties = {
  background: 'linear-gradient(90deg, #F0F0F0 0%, #F8F8F8 50%, #F0F0F0 100%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.4s infinite linear',
};
